export type AddressProviderId = "MOCK" | "GOOGLE_PLACES" | "CHECKOUT_API";

export interface AddressPoint {
  lat: number;
  lng: number;
}

export interface AddressSuggestion {
  id: string;
  displayName: string;
  secondaryText: string;
  point: AddressPoint | null;
  provider: AddressProviderId;
}

export interface AddressSearchProvider {
  readonly id: AddressProviderId;
  readonly ready: boolean;
  search(query: string, signal: AbortSignal): Promise<AddressSuggestion[]>;
  resolve(
    suggestion: AddressSuggestion,
    signal: AbortSignal,
  ): Promise<AddressSuggestion>;
}

export interface AddressMetricDelta {
  rawInputEvents: number;
  searchRequests: number;
  detailRequests: number;
  successful: number;
  failed: number;
  cancelled: number;
  avoided: number;
  mock: number;
  real: number;
  mapsLoads: number;
}

export const emptyAddressMetricDelta = (): AddressMetricDelta => ({
  rawInputEvents: 0,
  searchRequests: 0,
  detailRequests: 0,
  successful: 0,
  failed: 0,
  cancelled: 0,
  avoided: 0,
  mock: 0,
  real: 0,
  mapsLoads: 0,
});

const mockAddresses: readonly AddressSuggestion[] = [
  { id: "mock-renon", displayName: "Renon, Denpasar", secondaryText: "Denpasar, Bali", point: { lat: -8.65, lng: 115.22 }, provider: "MOCK" },
  { id: "mock-sanur", displayName: "Sanur, Denpasar", secondaryText: "Denpasar Selatan, Bali", point: { lat: -8.6938, lng: 115.2626 }, provider: "MOCK" },
  { id: "mock-kuta", displayName: "Kuta, Badung", secondaryText: "Badung, Bali · skenario 8 km", point: { lat: -8.65, lng: 115.16 }, provider: "MOCK" },
  { id: "mock-seminyak", displayName: "Seminyak, Badung", secondaryText: "Badung, Bali · skenario 12,4 km", point: { lat: -8.5915, lng: 115.23493 }, provider: "MOCK" },
  { id: "mock-ubud", displayName: "Ubud, Gianyar", secondaryText: "Gianyar, Bali · skenario 25 km", point: { lat: -8.5, lng: 115.3 }, provider: "MOCK" },
];

function cloneSuggestion(item: AddressSuggestion): AddressSuggestion {
  return { ...item, point: item.point ? { ...item.point } : null };
}

export function searchMockAddresses(query: string): AddressSuggestion[] {
  const terms = query.trim().toLocaleLowerCase("id-ID").split(/\s+/u);
  return mockAddresses
    .filter((item) => terms.every((term) =>
      `${item.displayName} ${item.secondaryText}`
        .toLocaleLowerCase("id-ID")
        .includes(term)))
    .map(cloneSuggestion);
}

export function resolveMockAddress(id: string): AddressSuggestion | undefined {
  const match = mockAddresses.find((item) => item.id === id);
  return match ? cloneSuggestion(match) : undefined;
}

export class AddressSearchController {
  private timer?: ReturnType<typeof setTimeout>;
  private active?: AbortController;
  private sequence = 0;
  private rawSinceDispatch = 0;
  private lastDispatched = "";
  private activeQuery = "";
  private cached?: { query: string; results: AddressSuggestion[]; expiresAt: number };

  constructor(
    private readonly provider: AddressSearchProvider,
    private readonly onResults: (results: AddressSuggestion[]) => void,
    private readonly onMetrics: (delta: AddressMetricDelta) => void,
    private readonly onError: (message: string) => void,
    private readonly debounceMs = 400,
    private readonly minimumLength = 3,
    private readonly options: {
      reuseMs?: number;
      onLoading?: (loading: boolean) => void;
      onReuse?: (reused: boolean) => void;
    } = {},
  ) {}

  input(query: string): void {
    const normalized = query.trim().replace(/\s+/gu, " ").toLocaleLowerCase("id-ID");
    if (this.active && this.activeQuery === normalized) {
      this.onMetrics({ ...emptyAddressMetricDelta(), rawInputEvents: 1, avoided: 1 });
      return; // Keep the existing in-flight request, rather than abort/restart it.
    }
    this.rawSinceDispatch += 1;
    this.sequence += 1;
    const sequence = this.sequence;
    if (this.timer) clearTimeout(this.timer);
    if (this.active) {
      this.active.abort();
      this.active = undefined;
    }
    this.options.onReuse?.(false);
    this.options.onLoading?.(normalized.length >= this.minimumLength);
    this.timer = setTimeout(() => { void this.dispatch(query, sequence); }, this.debounceMs);
  }

  private async dispatch(query: string, sequence: number): Promise<void> {
    this.timer = undefined;
    const rawInputEvents = this.rawSinceDispatch;
    this.rawSinceDispatch = 0;
    const normalized = query.trim().replace(/\s+/gu, " ").toLocaleLowerCase("id-ID");
    if (this.cached?.query === normalized && this.cached.expiresAt > Date.now()) {
      this.onResults(this.cached.results.map(cloneSuggestion));
      this.options.onReuse?.(true);
      this.options.onLoading?.(false);
      this.onMetrics({ ...emptyAddressMetricDelta(), rawInputEvents, avoided: rawInputEvents });
      return;
    }
    if (normalized.length < this.minimumLength ||
      (!this.options.reuseMs && normalized === this.lastDispatched)) {
      this.onResults([]);
      this.options.onLoading?.(false);
      this.onMetrics({ ...emptyAddressMetricDelta(), rawInputEvents, avoided: rawInputEvents });
      return;
    }
    this.lastDispatched = normalized;
    const controller = new AbortController();
    this.active = controller;
    this.activeQuery = normalized;
    const base: AddressMetricDelta = {
      ...emptyAddressMetricDelta(),
      rawInputEvents,
      searchRequests: 1,
      avoided: Math.max(0, rawInputEvents - 1),
      mock: this.provider.id === "MOCK" ? 1 : 0,
      real: this.provider.id === "GOOGLE_PLACES" ? 1 : 0,
    };
    try {
      const results = await this.provider.search(normalized, controller.signal);
      if (controller.signal.aborted || sequence !== this.sequence) {
        this.onMetrics({ ...base, cancelled: 1 });
        return;
      }
      this.onResults(results);
      if (this.options.reuseMs) {
        this.cached = { query: normalized, results: results.map(cloneSuggestion), expiresAt: Date.now() + this.options.reuseMs };
      }
      this.onMetrics({ ...base, successful: 1 });
    } catch (error) {
      if (
        controller.signal.aborted ||
        sequence !== this.sequence ||
        (error instanceof DOMException && error.name === "AbortError")
      ) {
        this.onMetrics({ ...base, cancelled: 1 });
        return;
      }
      this.onResults([]);
      this.lastDispatched = ""; // Failed queries must be retryable.
      this.onMetrics({ ...base, failed: 1 });
      this.onError(error instanceof Error ? error.message : "Pencarian alamat gagal.");
    } finally {
      if (this.active === controller) {
        this.active = undefined;
        this.activeQuery = "";
        this.options.onLoading?.(false);
      }
    }
  }

  dispose(): void {
    this.sequence += 1;
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.active?.abort();
    this.active = undefined;
    this.activeQuery = "";
    this.options.onLoading?.(false);
  }

  reset(): void {
    this.dispose();
    this.rawSinceDispatch = 0;
    this.lastDispatched = "";
    this.cached = undefined;
    this.options.onReuse?.(false);
  }
}
